// skipcq: JS-W1028, JS-0128
import React, { useCallback, useEffect, useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  Chip,
  Divider,
  IconButton,
  Paper,
  Stack,
  Typography,
} from '@mui/material';
import Grid from '@mui/material/Grid';
import EditIcon from '@mui/icons-material/Edit';
import { resolveStoredImageUrl } from './metricImageUpload';

function formatUpdatedAt(metric) {
  if (metric.updatedAt?.toDate) {
    return metric.updatedAt.toDate().toLocaleString();
  }
  if (metric.updatedAt?.__time__) {
    return new Date(metric.updatedAt.__time__).toLocaleString();
  }
  return '';
}

function DetailRow({ label, children, multiline }) {
  return (
    <Box>
      <Typography
        variant="caption"
        color="text.secondary"
        component="div"
        sx={{ fontWeight: 600 }}
      >
        {label}
      </Typography>
      <Typography
        variant="body2"
        component="div"
        sx={{
          wordBreak: 'break-word',
          ...(multiline ? { whiteSpace: 'pre-wrap' } : {}),
        }}
      >
        {children}
      </Typography>
    </Box>
  );
}

function MetricImagePreview({ filename, url }) {
  const [loadFailed, setLoadFailed] = useState(false);

  const handleImageError = useCallback(() => {
    setLoadFailed(true);
  }, []);

  useEffect(() => {
    setLoadFailed(false);
  }, [url]);

  const showImg = Boolean(url) && !loadFailed;

  return (
    <Box
      sx={{
        flexShrink: 0,
        width: 50,
        height: 50,
        boxSizing: 'border-box',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        borderRadius: 1,
        border: '1px solid',
        borderColor: 'divider',
        bgcolor: 'action.hover',
        p: 0.5,
      }}
    >
      {showImg ? (
        <Box
          component="img"
          src={url}
          alt={filename || 'Metric image'}
          referrerPolicy="no-referrer"
          onError={handleImageError}
          sx={{
            maxWidth: '100%',
            maxHeight: '100%',
            width: 'auto',
            height: 'auto',
            objectFit: 'contain',
            borderRadius: 1,
          }}
        />
      ) : (
        <Typography variant="caption" color="text.secondary" align="center">
          {filename
            ? url
              ? 'Could not load'
              : 'No URL'
            : 'No image'}
        </Typography>
      )}
    </Box>
  );
}

function MetricCardHeaderRow({ metric, onEditClick }) {
  return (
    <Box
      sx={{
        display: 'flex',
        flexWrap: 'wrap',
        alignItems: 'flex-start',
        justifyContent: 'space-between',
        gap: 1,
      }}
    >
      <Box sx={{ minWidth: 0, flex: '1 1 200px' }}>
        <Typography variant="subtitle1" fontWeight={600}>
          {metric.name ?? '—'}
        </Typography>
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ display: 'block', wordBreak: 'break-all' }}
        >
          {metric.id}
        </Typography>
      </Box>
      <Box
        sx={{
          display: 'flex',
          flexWrap: 'wrap',
          alignItems: 'center',
          justifyContent: 'flex-end',
          gap: 0.5,
        }}
      >
        <Chip
          label={`sort ${metric.sortOrder ?? 0}`}
          size="small"
          variant="outlined"
        />
        <IconButton
          aria-label={`Edit metric ${metric.id}`}
          size="small"
          onClick={onEditClick}
        >
          <EditIcon fontSize="small" />
        </IconButton>
      </Box>
    </Box>
  );
}

function MetricCardDetailsGrid({ metric, imageName, previewUrl }) {
  const mapping = (metric.appMapping ?? '').trim();
  return (
    <Grid
      container
      spacing={2}
      sx={{ alignItems: { xs: 'center', sm: 'stretch' } }}
    >
      <Grid item xs={12} sm={6} sx={{ minWidth: 0 }}>
        <Stack spacing={1.5}>
          <DetailRow label="Help text" multiline>
            {(metric.helpText ?? '').trim() !== ''
              ? metric.helpText
              : '—'}
          </DetailRow>
          <DetailRow label="App mapping">{mapping || '—'}</DetailRow>
          <DetailRow label="Image file">{metric.image ?? '—'}</DetailRow>
          {formatUpdatedAt(metric) && (
            <DetailRow label="Updated">{formatUpdatedAt(metric)}</DetailRow>
          )}
        </Stack>
      </Grid>

      <Grid
        item
        xs={12}
        sm={6}
        sx={{
          minWidth: 0,
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
        }}
      >
        <MetricImagePreview filename={imageName} url={previewUrl} />
      </Grid>
    </Grid>
  );
}

function MetricDefinitionCard({ metric, previewUrl, onEdit }) {
  const imageName = (metric.image ?? '').trim();
  const handleEditClick = useCallback(() => {
    onEdit(metric);
  }, [onEdit, metric]);

  return (
    <Card variant="outlined">
      <CardContent sx={{ '&:last-child': { pb: 2 } }}>
        <MetricCardHeaderRow metric={metric} onEditClick={handleEditClick} />
        <Divider sx={{ my: 1.5 }} />
        <MetricCardDetailsGrid
          metric={metric}
          imageName={imageName}
          previewUrl={previewUrl}
        />
      </CardContent>
    </Card>
  );
}

/**
 * Card list of metric definitions with edit action only.
 */
function MetricsTable({
  metrics,
  emptyMessage,
  imageUrlByName = {},
  onEdit,
}) {
  if (!metrics.length) {
    return (
      <Paper variant="outlined" sx={{ p: 3 }}>
        <Typography variant="body2" color="text.secondary">
          {emptyMessage ?? 'No metric definitions yet. Add one to get started.'}
        </Typography>
      </Paper>
    );
  }

  return (
    <Stack spacing={2}>
      {metrics.map((metric) => {
        const imageName = (metric.image ?? '').trim();
        const previewUrl = resolveStoredImageUrl(imageUrlByName, imageName);
        return (
          <MetricDefinitionCard
            key={metric.id}
            metric={metric}
            previewUrl={previewUrl}
            onEdit={onEdit}
          />
        );
      })}
    </Stack>
  );
}

export default MetricsTable;
